import { faCheck, faPenToSquare } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Box, Button, Group, Paper, Stack, Text } from '../../../core';
import { surfaceHeaderProps } from '../SurfaceHeader';

export interface EditQuoteChange {
  readonly label: string;
  // Optional by contract: the agent omits it when the quote carries no current
  // value for the field (e.g. a field-override row with no header source).
  readonly from?: string;
  readonly to: string;
}

/** Result of a successful Save — populated reactively by the host after the
 * surface action resolves (see A2UISurfaceList's result-injection branch),
 * never sent by the agent up front. */
export interface EditQuoteSaveResult {
  readonly message?: string;
  readonly premium?: string;
}

/** Written into the same `/results/<id>` path as `EditQuoteSaveResult` when
 * Save fails, so the existing reactive binding also carries failure — no
 * second schema-bound prop needed. Discriminated from a real result by the
 * `error` flag (an `EditQuoteSaveResult` never has one). */
export interface EditQuoteSaveError {
  readonly error: true;
  readonly message?: string;
}

export interface EditQuoteProps {
  readonly accountName: string;
  readonly quoteNumber?: string;
  readonly currentPremium?: string;
  readonly currentLimit?: string;
  readonly currentDeductible?: string;
  readonly changes?: readonly EditQuoteChange[];
  readonly previewedPremium?: string;
  readonly result?: EditQuoteSaveResult | EditQuoteSaveError;
  readonly pending?: boolean;
  readonly onSave?: () => void;
}

function isSaveError(
  value: EditQuoteSaveResult | EditQuoteSaveError | undefined
): value is EditQuoteSaveError {
  return Boolean(value) && (value as EditQuoteSaveError).error === true;
}

/** Every render/effect branch below reads exactly one of these four phases,
 * mirroring QuoteOption's GenerateState for the same reason: a single
 * derived state leaves no room for `saving`/`saved`/`saveFailed` to
 * disagree with each other. */
export type SaveState =
  | { readonly phase: 'idle' }
  | { readonly phase: 'saving' }
  | { readonly phase: 'success'; readonly result: EditQuoteSaveResult }
  | { readonly phase: 'error'; readonly message?: string };

/** Pure derivation, exported so phase transitions are covered by direct unit
 * tests rather than only inferred from rendered DOM assertions.
 *
 * Unlike QuoteOption's `isGenerateResult`, a saved-changes ack has no
 * analogous `newQuoteId` to validate against — there's no new entity being
 * created, so any non-error-shaped result counts as success. */
export function deriveSaveState(
  result: EditQuoteSaveResult | EditQuoteSaveError | undefined,
  pending: boolean
): SaveState {
  if (pending) return { phase: 'saving' };
  if (result && !isSaveError(result)) return { phase: 'success', result };
  if (result) {
    return {
      phase: 'error',
      message: isSaveError(result) ? result.message : undefined,
    };
  }
  return { phase: 'idle' };
}

function statusTextFor(state: Extract<SaveState, { phase: 'error' }>): string {
  return state.message || "Couldn't save changes — try again";
}

/**
 * Edit Quote — a single card previewing proposed changes (limit, deductible,
 * etc.) to an existing quote before the user commits them.
 */
export function EditQuote({
  accountName,
  quoteNumber,
  currentPremium,
  currentLimit,
  currentDeductible,
  changes,
  previewedPremium,
  result,
  pending,
  onSave,
}: EditQuoteProps) {
  const state = deriveSaveState(result, pending === true);
  const changeList = changes ?? [];
  const proposedPremium =
    state.phase === 'success'
      ? state.result.premium ?? previewedPremium
      : previewedPremium;

  return (
    <Paper
      radius="md"
      p="lg"
      withBorder
      style={{
        width: '100%',
        borderColor: 'var(--mantine-color-blue-2)',
      }}
    >
      <Group gap="xs" wrap="nowrap" {...surfaceHeaderProps('lg', 'sm')}>
        <FontAwesomeIcon
          icon={faPenToSquare}
          style={{
            color: 'var(--mantine-color-blue-6)',
            width: 18,
            height: 18,
          }}
        />
        <Text fw={700} fz={13} c="blue.6" style={{ letterSpacing: 0.5 }}>
          EDIT QUOTE
        </Text>
        <Text fz={13} c="dimmed">
          {accountName}
        </Text>
        {quoteNumber && (
          <Text fz={13} c="dimmed">
            · {quoteNumber}
          </Text>
        )}
      </Group>

      {(currentLimit || currentDeductible || currentPremium) && (
        <Text fz={12} c="dimmed" mb="lg">
          {`Current: limit ${currentLimit || 'n/a'}, deductible ${
            currentDeductible || 'n/a'
          }, Total Premium ${currentPremium || 'n/a'}`}
        </Text>
      )}

      <Box>
        <Text size="xs" c="dimmed" mb={4}>
          Proposed changes
        </Text>
        <Stack gap={4} mb="sm">
          {changeList.map((change) => (
            <Group
              key={change.label}
              justify="space-between"
              wrap="nowrap"
              gap={8}
            >
              <Text size="xs" c="dimmed">
                {change.label}
              </Text>
              <Text size="xs" fw={500}>
                {change.from ? `${change.from} → ${change.to}` : change.to}
              </Text>
            </Group>
          ))}
          <Group justify="space-between" wrap="nowrap" gap={8}>
            <Text size="xs" fw={600}>
              Proposed Premium
            </Text>
            <Text size="sm" fw={700}>
              {proposedPremium || 'n/a'}
            </Text>
          </Group>
        </Stack>
      </Box>

      <Group justify="space-between" align="center" wrap="nowrap" mt="sm">
        <Box>
          {state.phase === 'error' && (
            <Text size="xs" c="red.6" fs="italic">
              {statusTextFor(state)}
            </Text>
          )}
          {state.phase === 'success' && (
            <Group gap={4} wrap="nowrap">
              <FontAwesomeIcon
                icon={faCheck}
                style={{ width: 11, height: 11 }}
              />
              <Text size="xs" c="dimmed">
                {state.result.message || 'Saved'}
              </Text>
            </Group>
          )}
        </Box>
        <Button
          variant="light"
          color="blue"
          radius="md"
          size="xs"
          onClick={() => onSave?.()}
          loading={state.phase === 'saving'}
          disabled={state.phase === 'saving' || state.phase === 'success'}
          aria-label={`Save quote changes for ${accountName}`}
        >
          {state.phase === 'success' ? 'Saved' : 'Save'}
        </Button>
      </Group>
    </Paper>
  );
}
